import { useState, useCallback, useEffect, useRef } from 'react';
import { useMemoizedFn } from 'ahooks';
import { perfStart } from '../utils/perfMetrics';

interface UsePdfPaginationOptions {
    totalPages: number;
    initialPage?: number;
    onChange?: (pageNum: number, totalPages: number) => void;
}

export function usePdfPagination(options: UsePdfPaginationOptions) {
    const { totalPages, initialPage = 1, onChange } = options;
    // 确保 initialPage 是有效数字
    const validInitialPage = Number.isFinite(initialPage) && !isNaN(initialPage) ? initialPage : 1;
    const [pageNum, setPageNum] = useState<number>(validInitialPage);
    const prevTotalPagesRef = useRef<number>(0);
    const hasInitializedRef = useRef<boolean>(false);

    const safePageNumber = useCallback(
        (targetPage: number) => {
            let target = targetPage;
            // 处理 NaN、Infinity 等无效值
            if (!Number.isFinite(target) || isNaN(target)) {
                target = 1;
            }
            if (target < 1) target = 1;
            if (totalPages > 0 && target > totalPages) target = totalPages;
            return target;
        },
        [totalPages],
    );

    // Sync pageNum when totalPages changes
    useEffect(() => {
        if (totalPages > 0) {
            // When totalPages becomes available for the first time, initialize pageNum
            if (!hasInitializedRef.current && prevTotalPagesRef.current === 0) {
                const safe = safePageNumber(validInitialPage);
                setPageNum(safe);
                onChange?.(safe, totalPages);
                hasInitializedRef.current = true;
            } else if (!Number.isFinite(pageNum) || isNaN(pageNum)) {
                // 如果 pageNum 是无效值，重置为 1
                const safe = safePageNumber(1);
                setPageNum(safe);
                onChange?.(safe, totalPages);
            } else if (pageNum > totalPages) {
                // If current pageNum exceeds totalPages, clamp it
                const safe = safePageNumber(pageNum);
                setPageNum(safe);
                onChange?.(safe, totalPages);
            }
            prevTotalPagesRef.current = totalPages;
        } else {
            // Reset when totalPages becomes 0
            hasInitializedRef.current = false;
            prevTotalPagesRef.current = 0;
        }
    }, [totalPages, pageNum, validInitialPage, safePageNumber, onChange]);

    const jumpTo = useMemoizedFn((targetPage: number) => {
        const safe = safePageNumber(targetPage);
        if (safe !== pageNum) {
            perfStart('page_switch', String(safe), { from: pageNum, to: safe, totalPages });
            setPageNum(safe);
            onChange?.(safe, totalPages);
        }
    });

    /**
     * 静默更新页码，不触发 onChange 回调
     * 用于滚动时更新页码显示，避免触发其他副作用（如自动滚动）
     */
    const setPageNumSilent = useMemoizedFn((targetPage: number) => {
        const safe = safePageNumber(targetPage);
        if (safe !== pageNum) {
            setPageNum(safe);
            // 不调用 onChange，避免触发副作用
        }
    });

    const nextPage = useMemoizedFn(() => {
        jumpTo(pageNum + 1);
    });

    const prevPage = useMemoizedFn(() => {
        jumpTo(pageNum - 1);
    });

    return {
        pageNum,
        jumpTo,
        setPageNumSilent,
        nextPage,
        prevPage,
    };
}
